import React from "react";
import { Box, Button, Checkbox, Text } from "@showitinc/showit-wc";
import ProtorendererToggle from "../app2components/modals/ProtorendererToggle";
import PublishUpgradedIndicator from "./PublishUpgradedIndicator";
import ControlGroup from "./base/ControlGroup";
import Glyphicon from "./base/Glyphicon";
import connectToStores from "./providers/connectToStores";
import AppConfig from "../config/app";
import PublishStore from "../stores/PublishStore";
import SitePublishActions from "../actions/SitePublishActions";
import UserActions from "../actions/UserActions";
import AppConstants from "../constants/AppConstants";
import LoadingSpinner from "./LoadingSpinner";
import MessageUtils from "../utils/MessageUtils";
import SiteUtils from "../utils/SiteUtils";
import SubviewError from "./SubviewError";
import SubviewProcessing from "./SubviewProcessing";
import HelpTooltip from "./HelpTooltip";
import SubscribeButton from "./SubscribeButton";
import PublishSettingsButton from "./PublishSettingsButton";
import uiPrefix from "../utils/UiPrefixUtils";
import styles from "./PublishPanel.module.css";

class PublishPanel extends React.Component {
  constructor(props) {
    super(props);
    const data = PublishStore.getInitialState();
    const hasBlog = data.get("hasBlog");

    let publishBlog = false;

    if (hasBlog) {
      publishBlog = true;
    }

    this.state = { publishBlog };
  }

  _publish = () => {
    const settings = {
      publishBlog: this.state.publishBlog,
    };

    SitePublishActions.publishSite(settings);
    const { data } = this.props;
    const site = data.get("site");
    const domain = this._getDomain(site);

    UserActions.trackEvent({ event: "publish", meta: { domain } });
  };

  _close = () => {
    SitePublishActions.closeDialog();
  };

  _togglePublishBlog = () => {
    this.setState((prevState) => ({
      publishBlog: !prevState.publishBlog,
    }));
  };

  _showBlogError = () => {
    SitePublishActions.changeView(AppConstants.PUBLISH_VIEW_ERROR_BLOG);
  };

  /**
   * A popup for publishing a site and blog.
   * @param {string} domain
   * @param {Object} site 
   * @returns - React component initial view.
   */
  _getInitialView = (domain, site) => {
    const hasBlog = this.props.data.get("hasBlog");
    const { publishBlog } = this.state;

    let blogInput = null;
    if (hasBlog) {
      const blogDomain = site.get("blog_domain");
      blogInput = (
        <ControlGroup className={styles["blog-input"]}>
          {blogDomain !== domain ? (
            <Checkbox
              data-e2e={uiPrefix("publish-blog-checkbox")}
              className={`${uiPrefix("publish-blog-checkbox")} ${styles["blog-checkbox"]}`}
              id="publish_blog_cb"
              type="checkbox"
              checked={publishBlog}
              onSIChange={this._togglePublishBlog}
              label={`Publish Blog: ${blogDomain}`}
            />
          ) : (
            <Text>Blog: {blogDomain}</Text>
          )}
        </ControlGroup>
      );
    }

    return (
      <div className={`panel-column ${styles["publish-message-wrapper"]}`}>
        <div className={styles.message}>
          <div className={styles["icon-row"]}>
            <Glyphicon glyph="flash" className={styles.icon} />
          </div>
          <div className={styles.label}>Ready to publish to</div>
          <div className={styles.domain}>{domain}</div>
          {blogInput}
          <div className={styles["toggle-row"]}>
            <ProtorendererToggle />
            <PublishUpgradedIndicator />
          </div>
        </div>
        <div className={styles["footer-divider"]} />
        <div className={styles.footer}>
          <PublishSettingsButton />
          <div className={styles["footer-actions"]}>
            <Box mr="3">
              <Button
                variant="minimal"
                size="lg"
                data-e2e={uiPrefix("modal-cancel-btn")}
                className={uiPrefix("modal-cancel-btn")}
                onClick={this._close}
              >
                Cancel
              </Button>
            </Box>
            <Box mr="3">
              <Button
                variant="feature"
                size="lg"
                data-testid="publish-confirm-button"
                data-e2e={uiPrefix("modal-publish-btn")}
                className={uiPrefix("modal-publish-btn")}
                onClick={this._publish}
              >
                Publish
              </Button>
            </Box>
            <HelpTooltip tooltipKey="publish" />
          </div>
        </div>
      </div>
    );
  };

  _getPublishView = (domain) => (
    <div className={`view-centered ${styles["publish-message-wrapper"]}`}>
      <div className={styles["message-busy"]}>
        <div className={styles["icon-row-busy"]}>
          <LoadingSpinner />
        </div>
        <div className={styles["label-muted"]}>Publishing to</div>
        <div>{domain}</div>
      </div>
    </div>
  );

  _getPublishBlogView = (site) => {
    const blogDomain = site.get("blog_domain");
    return (
      <div className={`view-centered ${styles["centered-column"]}`}>
        <div className={styles["message-busy"]}>
          <div className={styles["icon-row-busy"]}>
            <LoadingSpinner />
          </div>
          <div className={styles["label-muted"]}>Publishing Blog to</div>
          <div>{blogDomain}</div>
        </div>
      </div>
    );
  };

  _getBlogPublishErrorView = (site, process) => {
    const message = process?.getIn(
      ["error", "message", "details"],
      process.getIn(["error", "message"], "")
    );

    return (
      <div className={`view-centered ${styles["centered-column"]}`}>
        <div className={styles["error-body"]}>
          <div className={styles["error-title"]}>
            <span className={`glyphicon glyphicon-warning_sign ${styles["warning-icon"]}`} />
            Error Publishing Blog
          </div>
          <div className={styles["error-message"]}>{message}</div>
          <div className={styles.tips}>
            <div className="panel-section-subheading">Troubleshooting Tips:</div>
            <ul className={styles["tips-list"]}>
              <li>Make sure Showit plugin is installed and updated in your WordPress admin</li>
              <li>Try to manually publish from your WordPress admin</li>
              <li>
                <a
                  href="http://learn.showit.co/building-your-blog/using-your-blog/live-blog-troubleshooting"
                  target="_blank"
                  rel="noreferrer"
                >
                  View troubleshooting article
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className={`${styles.footer} ${styles["footer-end"]}`}>
          <Button
            look="primary"
            data-e2e={uiPrefix("modal-close-btn")}
            className={uiPrefix("modal-close-btn")}
            onClick={this._close}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };

  _getProcessingView = () => {
    const message = "Your site is saving";

    return <SubviewProcessing message={message} />;
  };

  _getCompleteView = (domain, site, process) => {
    const hasBlog = this.props.data.get("hasBlog");

    const sitePublished = process.get("sitePublished");
    const blogPublished = process.get("blogPublished");

    let blogMessage = null;

    if (hasBlog) {
      if (blogPublished) {
        const blogDomain = site.get("blog_domain");
        blogMessage = (
          <div className={styles["blog-status"]}>Blog Design Published: {blogDomain}</div>
        );
      } else {
        blogMessage = (
          <div
            className={`${styles["blog-status"]} ${styles["blog-status-error"]}`}
            onClick={this._showBlogError}
          >
            <span className={`glyphicon glyphicon-warning_sign ${styles["warning-icon"]}`} />
            Blog design did not publish.{" "}
            <span className={styles["more-info"]}>Click here for more info</span>
          </div>
        );
      }
    }

    return (
      <div className={`view-centered ${styles["publish-message-wrapper"]}`}>
        <div className={styles["site-published-message"]}>
          <div className={styles["icon-row"]}>
            <span className={`glyphicon glyphicon-ok ${styles.icon}`} />
          </div>
          <div style={{ fontWeight: 600 }}>{MessageUtils.getSuccessExpression()}!</div>
          <div>
            Your site has been published.{" "}
            {MessageUtils.getExpression(AppConstants.EXPRESSIONS_VISIT)}:
          </div>
          <div>
            <a href={`http://${domain}`} target="_blank">
              {domain}
            </a>
          </div>
          {blogMessage}
        </div>
        <Box className={styles["footer-button-wrapper"]}>
          <Button
            variant="outline"
            data-e2e={uiPrefix("modal-close-btn")}
            className={uiPrefix("modal-close-btn")}
            onClick={this._close}
          >
            Close
          </Button>
        </Box>
      </div>
    );
  };

  _getErrorView = (process) => {
    let error = process.get("error", false);
    if (error) {
      error = error.toJS(); // convert from Immutable to work in view.
    }

    return <SubviewError error={error} onClose={this._close} />;
  };

  _getSubscribeView = () => (
    <div className={`view-centered ${styles["publish-message-wrapper"]}`}>
      <Text className={styles.header} variant="heading-1" variantStyle="strong">
        Ready To Publish?
      </Text>
      <Text className={styles.body} variant="body-1">
        Activate your subscription to publish your site to the web.
      </Text>
      <SubscribeButton>Subscribe Now</SubscribeButton>
    </div>
  );

  _getDomain = (site) => {
    const subdomain = site.get("subdomain", false);
    let domain = SiteUtils.createFullUrl(site);

    if (!domain) {
      if (subdomain) {
        domain = `${subdomain}.${AppConfig.siteDomain}`;
      } else {
        domain = `${site.get("key")}.${AppConfig.siteDomain}`;
      }
    }

    return domain;
  };

  render() {
    const { data } = this.props;
    const process = data.get("process");
    const site = data.get("site");

    const view = process.get("view");

    const domain = this._getDomain(site);

    // Taller for the initial view's toggle + warning banner; other views stay compact.
    const isInitialView = view === AppConstants.PUBLISH_VIEW_INITIAL;
    const panelStyle = {
      height: isInitialView ? "20.5rem" : "16.25rem",
    };

    let currentView = null;

    switch (view) {
      case AppConstants.PUBLISH_VIEW_PUBLISHING:
        currentView = this._getPublishView(domain);
        break;
      case AppConstants.PUBLISH_VIEW_PUBLISH_BLOG:
        currentView = this._getPublishBlogView(site);
        break;
      case AppConstants.PUBLISH_VIEW_COMPLETE:
        currentView = this._getCompleteView(domain, site, process);
        break;
      case AppConstants.PUBLISH_VIEW_SAVING:
        currentView = this._getProcessingView();
        break;
      case AppConstants.PUBLISH_VIEW_ERROR:
        currentView = this._getErrorView(process);
        break;
      case AppConstants.PUBLISH_VIEW_ERROR_BLOG:
        currentView = this._getBlogPublishErrorView(site, process);
        break;
      case AppConstants.PUBLISH_VIEW_SUBSCRIBE:
        currentView = this._getSubscribeView();
        break;
      case AppConstants.PUBLISH_VIEW_INITIAL:
        currentView = this._getInitialView(domain, site);
        break;
      default:
        break;
    }

    return (
      <div className="publish-panel" style={panelStyle}>
        {currentView}
      </div>
    );
  }
}

export default connectToStores(PublishPanel, [
  {
    store: PublishStore,
    state: { data: null },
  },
]);
